import {
  AttendanceSessionEnum,
  DATE_FORMAT_WEEK_DAY_MID_FNS,
  DEFAULT_DATE_FORMAT_FNS,
  GetRelationAttendanceAggregateResponse,
  GroupAttendanceAggregateSessionEntryResponse,
  RelationAttendanceAggregateSessionEntryResponse,
} from '@schooly/api';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { addDays, format, isAfter, parse, startOfISOWeek } from 'date-fns';

import { formatWeekRangeLabel, hasSessionEntry } from './buildGroupGrid';
import { SESSION_ORDER, WORKING_WEEK_LENGTH } from './constants';
import { isDateWithinValidity, ValidityInterval } from './studentValidity';
import {
  AttendanceGridCell,
  AttendanceGridCellValue,
  AttendanceGridColumn,
  AttendanceGridForStudent,
  AttendanceGridSession,
  AttendanceGridStudent,
} from './types';

const ISO_WEEKDAY_KEYS = Array.from({ length: 7 }, (_, i) =>
  format(addDays(startOfISOWeek(0), i), DATE_FORMAT_WEEK_DAY_MID_FNS).toLowerCase(),
);

const parseRegisterDate = (value: string): Date =>
  parse(value, DEFAULT_DATE_FORMAT_FNS, newDateTimezoneOffset());

const toGridEntry = (
  entry: RelationAttendanceAggregateSessionEntryResponse | null,
  session: AttendanceSessionEnum,
): GroupAttendanceAggregateSessionEntryResponse | null => (entry ? { ...entry, session } : null);

const isoDayToColumnKey = (isoDay: number): string => ISO_WEEKDAY_KEYS[isoDay - 1];

// `complete` and `has_register` are group-header concepts; weekday columns have no real value
// for them, so these are fillers to satisfy AttendanceGridColumn.
const buildColumn = (key: string, sessions: AttendanceGridSession[]): AttendanceGridColumn => ({
  key,
  date: null,
  label: key.toUpperCase(),
  sessions: sessions.map((session) => ({
    name: session,
    complete: true,
  })),
  has_register: true,
});

export type BuildStudentGridFromAggregateInput = {
  aggregate: GetRelationAttendanceAggregateResponse;
  validity: ValidityInterval[];
  student: AttendanceGridStudent;
  today: Date;
};

export const buildStudentGridFromAggregate = ({
  aggregate,
  validity,
  student,
  today,
}: BuildStudentGridFromAggregateInput): AttendanceGridForStudent => {
  const relationId = student.relation_id;
  const hasValidity = validity.length > 0;

  const lastDayIndex = aggregate.total.week_days.length
    ? Math.max(WORKING_WEEK_LENGTH - 1, ...aggregate.total.week_days.map((day) => day - 1))
    : WORKING_WEEK_LENGTH - 1;
  const columnKeys = ISO_WEEKDAY_KEYS.slice(0, lastDayIndex + 1);

  const hasPm = aggregate.rows.some((row) =>
    row.cells.some(
      (cell) => cell.meta.sessions.some((session) => session === 'pm') || !!cell.value.pm,
    ),
  );
  const sessionUnion: AttendanceGridSession[] = hasPm ? ['am', 'pm'] : ['am'];
  const columns = columnKeys.map((key) => buildColumn(key, sessionUnion));

  const buildWeekCells = (
    weekRow: GetRelationAttendanceAggregateResponse['rows'][number],
  ): AttendanceGridCell[] => {
    const weekStart = parseRegisterDate(weekRow.meta.start_date);
    const cellsByColumnKey = new Map(
      weekRow.cells.map((cell) => [isoDayToColumnKey(cell.meta.day), cell] as const),
    );

    return columnKeys.map((key) => {
      const cell = cellsByColumnKey.get(key);
      const date = cell?.value.register?.date
        ? parseRegisterDate(cell.value.register.date)
        : addDays(weekStart, ISO_WEEKDAY_KEYS.indexOf(key));

      const isFuture = isAfter(date, today);

      if (hasValidity && !isDateWithinValidity(date, validity)) {
        return {
          column_key: key,
          kind: 'out_of_day' as const,
          editable: false,
        };
      }

      if (!cell?.value.register) {
        return { column_key: key, kind: 'blocked' as const, editable: false };
      }

      const cellSessions = SESSION_ORDER.filter((session) =>
        cell.meta.sessions.some((s) => String(s) === session),
      );

      const value: AttendanceGridCellValue = {
        justification: cell.value.justification,
        absence_request: cell.value.am?.absence_request ?? cell.value.pm?.absence_request ?? null,
        am: toGridEntry(cell.value.am, AttendanceSessionEnum.Am),
        pm: toGridEntry(cell.value.pm, AttendanceSessionEnum.Pm),
      };

      return {
        column_key: key,
        kind: 'normal' as const,
        editable: !isFuture && hasSessionEntry(value),
        sessions: cellSessions.length ? cellSessions : undefined,
        data: {
          register_id: cell.value.register.id,
          date: cell.value.register.date,
          relation_id: relationId,
          cell: value,
        },
      };
    });
  };

  const rows = aggregate.rows
    .map((weekRow) => ({
      key: weekRow.meta.start_date,
      label: formatWeekRangeLabel(weekRow.meta.start_date, weekRow.meta.end_date),
      student,
      cells: buildWeekCells(weekRow),
    }))
    .sort((a, b) => b.key.localeCompare(a.key));

  return { columns, rows };
};
